import React, { useState, useMemo } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Typography, Chip, IconButton, Tooltip, CircularProgress, Box,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Alert, Snackbar, Grid, ToggleButtonGroup, ToggleButton
} from "@mui/material";
import { PictureAsPdf, Edit, Cancel } from "@mui/icons-material";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

export default function TablaCompras({ compras, loading, onRecargar }) {
  const [dialogModificar, setDialogModificar] = useState({ open: false, compra: null });
  const [dialogAnular, setDialogAnular] = useState({ open: false, compra: null });
  const [motivoAnulacion, setMotivoAnulacion] = useState("");
  const [observacionesModificacion, setObservacionesModificacion] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [procesando, setProcesando] = useState(false);

  // Filtros
  const [filtroFecha, setFiltroFecha] = useState("todos"); // hoy, semana, mes, personalizado, todos
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  // Filtrar compras por fecha
  const comprasFiltradas = useMemo(() => {
    if (filtroFecha === "todos") return compras;

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);

    return compras.filter(compra => {
      const fechaStr = compra.fecha_emision || compra.factura?.fechaEmision || "";
      if (!fechaStr) return true;
      const fecha = new Date(fechaStr + "T00:00:00");

      if (filtroFecha === "hoy") {
        return fecha.toDateString() === hoy.toDateString();
      }
      if (filtroFecha === "semana") {
        const hace7 = new Date(hoy);
        hace7.setDate(hace7.getDate() - 7);
        return fecha >= hace7;
      }
      if (filtroFecha === "mes") {
        return fecha.getMonth() === hoy.getMonth() && fecha.getFullYear() === hoy.getFullYear();
      }
      if (filtroFecha === "personalizado") {
        const desde = fechaDesde ? new Date(fechaDesde + "T00:00:00") : null;
        const hasta = fechaHasta ? new Date(fechaHasta + "T23:59:59") : null;
        if (desde && fecha < desde) return false;
        if (hasta && fecha > hasta) return false;
        return true;
      }
      return true;
    });
  }, [compras, filtroFecha, fechaDesde, fechaHasta]);

  const descargarPDFCompra = async (compraId, numeroComprobante) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/contabilidad/compras/${compraId}/pdf`,
        { headers: getAuthHeaders(), responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Compra_${numeroComprobante || compraId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showSnackbar("Error al descargar el PDF", "error");
    }
  };

  const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });

  const confirmarModificacion = async () => {
    if (!dialogModificar.compra) return;
    setProcesando(true);
    try {
      await axios.put(
        `${API_URL}/api/contabilidad/compras/${dialogModificar.compra.id}`,
        { observaciones: observacionesModificacion, fecha_modificacion: new Date().toISOString() },
        { headers: getAuthHeaders() }
      );
      showSnackbar("Compra modificada exitosamente", "success");
      setDialogModificar({ open: false, compra: null });
      onRecargar();
    } catch (error) {
      showSnackbar(error.response?.data?.error || "Error al modificar compra", "error");
    } finally {
      setProcesando(false);
    }
  };

  const confirmarAnulacion = async () => {
    if (!dialogAnular.compra || !motivoAnulacion.trim()) {
      showSnackbar("Debe proporcionar un motivo de anulación", "error");
      return;
    }
    setProcesando(true);
    try {
      await axios.put(
        `${API_URL}/api/contabilidad/compras/${dialogAnular.compra.id}/anular`,
        { motivo_anulacion: motivoAnulacion },
        { headers: getAuthHeaders() }
      );
      showSnackbar("Compra anulada exitosamente", "success");
      setDialogAnular({ open: false, compra: null });
      setMotivoAnulacion("");
      onRecargar();
    } catch (error) {
      showSnackbar(error.response?.data?.error || "Error al anular compra", "error");
    } finally {
      setProcesando(false);
    }
  };

  // Totales
  const totalFiltrado = comprasFiltradas.reduce((sum, c) => sum + parseFloat(c.totales?.importeTotal || c.total || 0), 0);

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {/* Filtros de fecha */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <ToggleButtonGroup
              value={filtroFecha}
              exclusive
              onChange={(e, v) => v && setFiltroFecha(v)}
              size="small"
            >
              <ToggleButton value="hoy">Hoy</ToggleButton>
              <ToggleButton value="semana">Semana</ToggleButton>
              <ToggleButton value="mes">Mes</ToggleButton>
              <ToggleButton value="personalizado">Personalizado</ToggleButton>
              <ToggleButton value="todos">Todos</ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          {filtroFecha === "personalizado" && (
            <>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth size="small" type="date" label="Desde"
                  value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth size="small" type="date" label="Hasta"
                  value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} md={filtroFecha === "personalizado" ? 12 : 6}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, alignItems: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {comprasFiltradas.length} compras
              </Typography>
              <Chip label={`Total: $${totalFiltrado.toFixed(2)}`} color="primary" />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {comprasFiltradas.length === 0 ? (
        <Box sx={{ textAlign: "center", p: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No hay compras en el período seleccionado
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                <TableCell>Fecha</TableCell>
                <TableCell>Proveedor</TableCell>
                <TableCell>RUC</TableCell>
                <TableCell>Número Factura</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comprasFiltradas.map((compra) => (
                <TableRow key={compra.id} hover>
                  <TableCell>{compra.fecha_emision || compra.factura?.fechaEmision || "-"}</TableCell>
                  <TableCell>{compra.proveedor?.razonSocial || compra.proveedor?.razon_social || "-"}</TableCell>
                  <TableCell>{compra.proveedor?.ruc || "-"}</TableCell>
                  <TableCell>{compra.factura?.numeroComprobante || compra.numero_factura || "-"}</TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={600}>${compra.totales?.importeTotal || compra.total || 0}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={compra.estado === "ANULADA" ? "Anulada" : "Registrada"}
                      color={compra.estado === "ANULADA" ? "error" : "success"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                      <Tooltip title="Descargar PDF">
                        <IconButton size="small" color="primary"
                          onClick={() => descargarPDFCompra(compra.id, compra.factura?.numeroComprobante)}>
                          <PictureAsPdf />
                        </IconButton>
                      </Tooltip>
                      {compra.estado !== "ANULADA" && (
                        <>
                          <Tooltip title="Modificar">
                            <IconButton size="small" color="info"
                              onClick={() => { setDialogModificar({ open: true, compra }); setObservacionesModificacion(compra.observaciones || ""); }}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Anular">
                            <IconButton size="small" color="error"
                              onClick={() => { setDialogAnular({ open: true, compra }); setMotivoAnulacion(""); }}>
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog Modificar */}
      <Dialog open={dialogModificar.open} onClose={() => setDialogModificar({ open: false, compra: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Modificar Compra</DialogTitle>
        <DialogContent>
          {dialogModificar.compra && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Factura:</strong> {dialogModificar.compra.factura?.numeroComprobante}
              </Typography>
              <TextField fullWidth label="Observaciones" multiline rows={3}
                value={observacionesModificacion} onChange={(e) => setObservacionesModificacion(e.target.value)}
                sx={{ mt: 2 }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogModificar({ open: false, compra: null })}>Cancelar</Button>
          <Button onClick={confirmarModificacion} variant="contained" disabled={procesando}>
            {procesando ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Anular */}
      <Dialog open={dialogAnular.open} onClose={() => setDialogAnular({ open: false, compra: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Anular Compra</DialogTitle>
        <DialogContent>
          {dialogAnular.compra && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Factura:</strong> {dialogAnular.compra.factura?.numeroComprobante} — ${dialogAnular.compra.totales?.importeTotal}
              </Typography>
              <TextField fullWidth label="Motivo de Anulación *" multiline rows={3}
                value={motivoAnulacion} onChange={(e) => setMotivoAnulacion(e.target.value)}
                sx={{ mt: 2 }} required />
              <Alert severity="warning" sx={{ mt: 2 }}>
                Esta acción es irreversible y generará un asiento de reversión.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogAnular({ open: false, compra: null })}>Cancelar</Button>
          <Button onClick={confirmarAnulacion} variant="contained" color="error" disabled={procesando || !motivoAnulacion.trim()}>
            {procesando ? "Anulando..." : "Anular"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
